import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import { requireWorkspaceId } from '../shared/workspace';
import { parseSubject, subjectKeys, type SubjectDetail } from './entity';

/**
 * Subject read. Single by-id detail, following the `services/db/action`
 * pattern: an exported `getSubjectQueryOptions` factory plus a thin suspense
 * hook.
 *
 * Consumers FK-gate on `Delivery.subject_id` (only mount when present) and wrap
 * the section in `AppErrorBoundary` — a deleted/inaccessible subject (404/403)
 * must degrade to nothing, not tear down the delivery detail. `retry: false`
 * surfaces that failure to the boundary immediately instead of retrying.
 */

const STALE_TIME = 60_000;

/** @public */
export async function getSubject(workspaceId: number, id: string): Promise<SubjectDetail> {
  const data = await hydraUiApi.subjects.get({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  // The wire marks the sideload arrays required, but this API returns null for
  // clusters it hasn't populated (same spec/runtime gap as services/db/activity-log
  // and campaign/populate). Normalize them here so consumers can `.find`/`.length`
  // without a null crash that the surrounding error boundary would silently swallow.
  return {
    ...data,
    subject: parseSubject(data.subject),
    subject_steps: data.subject_steps ?? [],
    actions: data.actions ?? [],
    campaigns: data.campaigns ?? [],
  };
}

/** @public */
export const getSubjectQueryOptions = (workspaceId: number, id: string) =>
  queryOptions({
    queryKey: subjectKeys.detail(workspaceId, id),
    queryFn: () => getSubject(workspaceId, id),
    staleTime: STALE_TIME,
    retry: false,
  });

/** @public */
export const useSubject = ({ id }: { id: string }) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery(getSubjectQueryOptions(workspaceId, id));
};
